import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Input, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IIHCProfessionalExperience } from 'app/models/licenses/in-home-care/ihc-professional-experience';
import { IStatus } from 'app/models/licenses/status';
import { AdministratorApplicationService } from 'app/services/license/in-home-care/administrator-application.service';

@Component({
  selector: 'professional-experience',
  templateUrl: './professional-experience.component.html',
  styleUrls: ['./professional-experience.component.scss'],
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})
export class ProfessionalExperienceComponent {
  @Input() applicationId: number = 0;
  @Input() isChange: boolean = false;
  @Input() statuses: IStatus[] = [];
  @Input() isEditable: boolean = false;

  public includeArchived: boolean = false;
  public addingExperience: boolean = false;
  public isTempExperience: boolean = false;
  public experience: IIHCProfessionalExperience = {
    id: 0,
    applicationId: 0,
    certification: '',
    dateReceived: '',
    certificateNumber: '',
    certificateExpirationDate: '',
    licensedInOregon: null,
    licensedInAnotherState: null,
    otherLicensedState: '',
    archived: false
  }
  public experiences: IIHCProfessionalExperience[] = [];
  public experienceForm = new FormGroup({
    certification: new FormControl('', Validators.required),
    dateReceived: new FormControl<Date>(null, Validators.required),
    certificateNumber: new FormControl(''),
    certificateExpirationDate: new FormControl<Date>(null),
    licensedInOregon: new FormControl(null),
    licensedInAnotherState: new FormControl(null),
    otherLicensedState: new FormControl(''),
  });

  public experienceColumns: string [] = ['certification', 'dateReceived', 'certificateNumber', 'actions'];
  public experienceSource = new MatTableDataSource<IIHCProfessionalExperience>(this.experiences);
  @ViewChild('experiencePaginator', { static: false })
  set experiencePaginator(value: MatPaginator | undefined) {
    // Conditional views recreate these controls independently of data loading.
    this.experienceSource.paginator = value ?? null;
  }
  get experiencePaginator(): MatPaginator {
    return this.experienceSource.paginator!;
  }
  @ViewChild('experienceSort', { static: false })
  set experienceSort(value: MatSort | undefined) {
    // Conditional views recreate these controls independently of data loading.
    this.experienceSource.sort = value ?? null;
  }
  get experienceSort(): MatSort {
    return this.experienceSource.sort!;
  }

  public sharedService = inject(SharedService);
  public applicationService = inject(AdministratorApplicationService);

  ngOnInit(): void {
    this.getExperience(this.includeArchived);
  }

  ngOnDestroy(): void {
    if(this.addingExperience && !this.locked){
      this.saveExperience();
    }
  }

  getExperience(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.applicationService.getProfessionalExperiences(this.applicationId, includeArchived).subscribe({
      next: response => this.experiences = response,
      error: e => console.log('error getting Professional experience', e),
      complete: () => this.updateExperienceTable()
    })
  }

  updateExperienceTable(): void {
    this.experienceSource.data = this.experiences;
  }

  getEmptyExperience(): IIHCProfessionalExperience {
    return {
      id: 0,
      applicationId: 0,
      certification: '',
      dateReceived: '',
      certificateNumber: '',
      certificateExpirationDate: '',
      licensedInOregon: null,
      licensedInAnotherState: null,
      otherLicensedState: '',
      archived: false
    }
  }

  selectExperience(experience: IIHCProfessionalExperience): void {
    this.updateExperienceForm(experience);
    this.experience = experience;
    this.addingExperience = true;
  }

  toggleExperienceForm(adding: boolean): void {
    this.addingExperience = adding;
    this.updateExperienceForm(this.getEmptyExperience());
    if(!adding){
      if(this.isTempExperience){
        this.deleteExperience(this.experience.id);
      }
      else{
        this.updateExperienceTable();
        this.experience = this.getEmptyExperience();
      }
    }
  }

  updateExperience(): void {
    let form = this.experienceForm.value;
    this.experience.certification = form.certification;
    this.experience.dateReceived = form.dateReceived?.toDateString();
    this.experience.certificateNumber = form.certificateNumber;
    this.experience.certificateExpirationDate = form.certificateExpirationDate?.toDateString();
    this.experience.licensedInOregon = form.licensedInOregon;
    this.experience.licensedInAnotherState = form.licensedInAnotherState;
    this.experience.otherLicensedState = form.otherLicensedState;
  }

  updateExperienceForm(experience: IIHCProfessionalExperience): void {
    this.experienceForm.reset({
      certification: experience.certification,
      certificateNumber: experience.certificateNumber,
      licensedInOregon: experience.licensedInOregon,
      licensedInAnotherState: experience.licensedInAnotherState,
      otherLicensedState: experience.otherLicensedState
    });

    if(experience.dateReceived){
      this.experienceForm.get('dateReceived').setValue(new Date(experience.dateReceived));
    }
    if(experience.certificateExpirationDate){
      this.experienceForm.get('certificateExpirationDate').setValue(new Date(experience.certificateExpirationDate));
    }
  }

  createExperience(): void {
    this.experience.applicationId = this.applicationId;
    this.applicationService.createProfessionalExperience(this.experience).subscribe({
      next: response => {
        this.experience = response;
        this.experiences.push(response);
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Create Professional Experience");
        console.log('error creating Professional experience', e);
      },
      complete: () => {
        this.toggleExperienceForm(true);
        this.isTempExperience = true;
      }
    });
  }

  saveExperience(): void {
    this.updateExperience();
    this.applicationService.updateProfessionalExperience(this.experience).subscribe({
      complete: () => {
        this.sharedService.toastr.success("Updated Your Professional Experience");
        this.isTempExperience = false;
        this.toggleExperienceForm(false);
      },
      error: e => {
        this.sharedService.toastr.error("Could Not Save Experience");
        console.log('error updating Professional experience', e);
      }
    });
  }

  archiveExperience(experience: IIHCProfessionalExperience, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this Professional experience entry?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.applicationService.archiveProfessionalExperience(experience.id, archive).subscribe({
            complete: () => {
              experience.archived = archive;
              if(archive){
                this.experiences = this.experiences.filter(e => e.id !== experience.id);
              }
              this.toggleExperienceForm(false);
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive Experience");
              console.log('error archiving Professional experience', e);
            }
          });
        }
      }
    );
  }

  confirmDelete(experienceId: number){
    this.sharedService.openConfirm('Delete this Professional experience entry?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.deleteExperience(experienceId);
        }
      }
    );
  }

  deleteExperience(experienceId: number): void {
    this.applicationService.deleteProfessionalExperience(experienceId).subscribe({
      complete: () => {
        this.isTempExperience = false;
        this.experiences = this.experiences.filter(e => e.id !== experienceId);
        this.updateExperienceTable();
        this.experience = this.getEmptyExperience();
      },
      error: e => console.log('error deleting Professional experience', e)
    });
  }

  public get locked(): boolean {
    return !this.statuses.some(s => s.label === this.sharedService.started) && !this.isEditable;
  }

  public get approved(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.approved);
  }
}
